import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Waves,
  Utensils,
  MapPin,
  Calendar,
  CheckCircle,
  Phone,
  ArrowRight,
  Sparkles,
  Wifi,
  Tv,
  Coffee,
  ShieldCheck,
  Clock,
  Car,
  ChevronDown,
  Compass,
  Smile,
  Heart
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import BookingSearchModal from '../../components/BookingSearchModal';
import { trackBookNow, trackWhatsAppClick, trackPageView } from '../../utils/analytics';
import { buildLandingRoomCards } from '../../utils/landingRooms';
import LandingRoomCardGrid from '../../components/public/LandingRoomCardGrid';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FamilyHotelBatu = () => {
  const navigate = useNavigate();
  const [showBookingSearch, setShowBookingSearch] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [content, setContent] = useState({});
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    // Update Title & Meta for SEO
    document.title = "Family Hotel di Batu Malang | Spencer Green Hotel Dekat Selecta";
    trackPageView('/family-hotel-batu');

    // Fetch dynamic content from API
    const fetchPageContent = async () => {
      try {
        const res = await axios.get(`${API_URL}/content/family_hotel`);
        if (Array.isArray(res.data)) {
          const map = {};
          res.data.forEach(item => {
            map[item.section] = item.content;
          });
          setContent(map);
        }
      } catch (err) {
        console.error('Failed to fetch family_hotel content:', err);
      }
    };

    // Fetch rooms from API
    const fetchRooms = async () => {
      try {
        const res = await axios.get(`${API_URL}/rooms`);
        if (res.data && res.data.length > 0) {
          setRooms(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch rooms:', err);
      }
    };

    fetchPageContent();
    fetchRooms();
  }, []);

  const handleBookNow = (roomName = 'Family Room') => {
    trackBookNow(`Family Landing - ${roomName}`);
    setShowBookingSearch(true);
  };

  const handleWhatsApp = (topic = 'Tanya Paket Keluarga') => {
    trackWhatsAppClick(`Family Landing - ${topic}`);
    const message = `Halo Spencer Green Hotel Batu, saya ingin menanyakan paket kamar keluarga dan fasilitas liburan keluarga di hotel.`;
    window.open(`https://wa.me/6281130700206?text=${encodeURIComponent(message)}`, '_blank');
  };

  const heroContent = content.hero || {};
  const showcaseContent = content.showcase || {};
  const ctaContent = content.cta || {};

  const familyRooms = [
    {
      name: 'Executive Suite - Family Setup',
      capacity: '3 - 4 Orang',
      bed: '1 Super King Bed + Sofa Bed / Extra Bed Ready',
      size: '42 m²',
      image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
      description: 'Pilihan paling ideal untuk keluarga! Ruang kamar ekstra luas dengan living area, smart TV, balkon pribadi dengan view pegunungan, dan bathtub air hangat untuk mandi si kecil.',
      features: ['Balkon View Pegunungan', 'Bathtub Air Panas 24 Jam', 'Living Area & Sofa', 'Smart TV & WiFi Kencang', 'Kulkas Mini & Tea Maker'],
      basePrice: 1800000
    },
    {
      name: 'Deluxe Room Mountain View',
      capacity: '2 Dewasa + 1-2 Anak',
      bed: 'King Bed atau Twin Bed',
      size: '32 m²',
      image: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
      description: 'Kamar nyaman dan berhawa sejuk alami dengan pemandangan perbukitan hijau. Dilengkapi water heater stabil, AC dingin, dan balkon santai untuk menikmati udara segar Batu.',
      features: ['Balkon Privat Santai', 'Water Heater Stabil', 'Air Hangat & Amenities Lengkap', 'WiFi Kencang', 'Opsi Connecting Room'],
      basePrice: 1200000
    },
    {
      name: 'Superior Room - Cozy Stay',
      capacity: '2 Dewasa + 1 Anak',
      bed: 'Double Bed / Twin Bed',
      size: '28 m²',
      image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800',
      description: 'Pilihan hemat dan nyaman untuk liburan keluarga kecil. Akses mudah ke kolam renang dan restoran, bersih, tenang, dan aman.',
      features: ['Akses Dekat Kolam Renang', 'Shower Air Hangat', 'TV Kabel Channel Anak', 'Coffee & Tea Set', 'Ruangan Bersih & Higienis'],
      basePrice: 850000
    }
  ];

  const highlights = [
    {
      icon: <Waves className="w-6 h-6 text-emerald-600" />,
      title: content.highlight_1?.title || "Kolam Renang Outdoor Ramah Anak",
      desc: content.highlight_1?.desc || "Kolam renang luas dengan air jernih dan pemandangan perbukitan hijau. Ada area dangkal yang nyaman untuk anak berenang serta kursi santai untuk orang tua."
    },
    {
      icon: <Utensils className="w-6 h-6 text-emerald-600" />,
      title: content.highlight_2?.title || "Sarapan Buffet Nusantara & Kids-Friendly",
      desc: content.highlight_2?.desc || "Menu prasmanan lezat di Kenari Restaurant: bubur ayam gurih, pancake/roti bakar, sereal, telur, buah segar, susu, dan jus yang disukai anak-anak."
    },
    {
      icon: <Smile className="w-6 h-6 text-emerald-600" />,
      title: content.highlight_3?.title || "Suasana Asri, Tenang & Aman",
      desc: content.highlight_3?.desc || "Halaman hijau terbuka untuk anak berjalan santai di pagi hari dengan udara sejuk bebas polusi khas Punten Bumiaji."
    },
    {
      icon: <MapPin className="w-6 h-6 text-emerald-600" />,
      title: content.highlight_4?.title || "Hanya 5 Menit ke Wisata Selecta",
      desc: content.highlight_4?.desc || "Lokasi emas di Jl. Raya Punten No. 86. Langsung lurus menuju Taman Rekreasi Selecta tanpa terjebak macet pusat kota Batu."
    }
  ];

  const familyDestinations = [
    {
      name: content.dest_1?.name || "Taman Rekreasi Selecta",
      distance: content.dest_1?.distance || "2.8 km",
      time: content.dest_1?.time || "± 5 Menit",
      image: content.dest_1?.image || "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600",
      desc: content.dest_1?.desc || "Taman bunga legendaris dengan waterpark anak, perahu ayun, sky bike, dan udara sejuk pegunungan."
    },
    {
      name: content.dest_2?.name || "Wisata Petik Apel Punten",
      distance: content.dest_2?.distance || "1.2 km",
      time: content.dest_2?.time || "± 3 Menit",
      image: content.dest_2?.image || "https://images.unsplash.com/photo-1560806887-1e4cd0b6cbd6?w=600",
      desc: content.dest_2?.desc || "Edukasi seru memetik apel Batu langsung dari kebun bersama anak dan keluarga."
    },
    {
      name: content.dest_3?.name || "Coban Talun & Oyot",
      distance: content.dest_3?.distance || "4.5 km",
      time: content.dest_3?.time || "± 8 Menit",
      image: content.dest_3?.image || "https://images.unsplash.com/photo-1511497584788-87676104235f?w=600",
      desc: content.dest_3?.desc || "Keindahan air terjun alami dengan spot foto menarik, taman bunga, dan wahana outdoor ramah keluarga."
    },
    {
      name: content.dest_4?.name || "Batu Secret Zoo & Jatim Park 2",
      distance: content.dest_4?.distance || "7.5 km",
      time: content.dest_4?.time || "± 18 Menit",
      image: content.dest_4?.image || "https://images.unsplash.com/photo-1534567153574-2b12153a87f0?w=600",
      desc: content.dest_4?.desc || "Kebun binatang modern bertaraf internasional dan Museum Satwa terlengkap di Indonesia."
    }
  ];

  const faqs = [
    {
      q: "Apakah Spencer Green Hotel cocok untuk liburan keluarga bersama anak?",
      a: "Sangat cocok! Kami memiliki kolam renang outdoor ramah anak, pilihan kamar luas berkapasitas keluarga, sarapan buffet dengan menu favorit anak, serta area hotel yang tenang dan aman dengan udara pegunungan yang sangat bersih."
    },
    {
      q: "Berapa jarak dari Spencer Green Hotel ke Taman Rekreasi Selecta?",
      a: "Sangat dekat! Spencer Green Hotel berada di Jalan Raya Punten No. 86, tepat di jalur utama menuju Selecta dengan jarak hanya sekitar 2.8 km atau 5 menit berkendara lurus. Anda dapat berangkat pagi hari tanpa terkena macet pusat kota."
    },
    {
      q: "Apakah tersedia fasilitas air panas di setiap kamar mandi?",
      a: "Ya, seluruh kamar di Spencer Green Hotel dilengkapi dengan water heater (air panas) bertekanan stabil 24 jam sehingga anak-anak dan keluarga dapat mandi dengan hangat dan nyaman meski udara Batu sedang dingin."
    },
    {
      q: "Apakah bisa request extra bed atau connecting room?",
      a: "Bisa. Anda dapat memesan extra bed atau mengajukan permintaan kamar bersebelahan/connecting room saat reservasi atau langsung menghubungi staf resepsionis kami melalui WhatsApp."
    },
    {
      q: "Bagaimana cara melakukan reservasi kamar keluarga?",
      a: "Anda dapat klik tombol 'Cek Harga & Ketersediaan' di halaman ini untuk mengecek tarif langsung dan memesan online secara aman, atau klik tombol 'WhatsApp' untuk dibantu oleh tim reservasi kami."
    }
  ];

  return (
    <div className="bg-emerald-50/20 text-gray-800">
      {/* Schema.org Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Hotel",
          "name": "Spencer Green Hotel Batu - Family Hotel",
          "description": "Hotel ramah keluarga di Batu Malang dekat Taman Rekreasi Selecta dengan kolam renang, sarapan buffet, dan kamar keluarga luas.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Jl. Raya Punten No.86, Bumiaji",
            "addressLocality": "Kota Batu",
            "addressRegion": "Jawa Timur",
            "postalCode": "65338",
            "addressCountry": "ID"
          },
          "telephone": "+6281130700206",
          "priceRange": "Rp 600.000 - Rp 1.800.000",
          "url": "https://spencergreenhotel.com/family-hotel-batu"
        })}
      </script>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
        {/* Hero Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroContent.image || "https://res.cloudinary.com/dgfjos8xa/image/upload/v1767362992/spencer-green-hotel-batu-malang-main-pool-view-building-night_u2ienp.webp"}
            alt={heroContent.imageAlt || "Family Hotel di Batu Malang - Spencer Green Hotel"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center sm:text-left">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-emerald-600/90 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                {heroContent.badge || "Hotel Pilihan Utama Liburan Keluarga di Batu Malang"}
              </div>

              <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                {heroContent.title || "Liburan Keluarga Nyaman & Tenang di Kota Batu"}
              </h1>

              <p className="text-emerald-100 text-base sm:text-lg lg:text-xl leading-relaxed mb-8">
                {heroContent.subtitle || "Ciptakan momen tak terlupakan bersama buah hati di Spencer Green Hotel Batu. Kamar keluarga luas berpenyejuk alami, kolam renang outdoor segar, sarapan lezat, dan lokasi strategis hanya 5 menit ke Taman Rekreasi Selecta."}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  onClick={() => handleBookNow('Hero Family')}
                  className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Cek Harga & Ketersediaan Kamar
                </Button>

                <Button
                  onClick={() => handleWhatsApp('Hero Konsultasi')}
                  variant="outline"
                  className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border-white/30 font-medium text-base px-6 py-6 rounded-full backdrop-blur-sm"
                >
                  <Phone className="w-5 h-5 mr-2 text-emerald-400" />
                  Tanya Paket Keluarga (WA)
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10 pt-8 border-t border-white/20 text-white/90 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>5 Menit ke Selecta</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Kolam Renang Anak & Dewasa</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Air Panas 24 Jam Stabil</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Sarapan Buffet Keluarga</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights Bar */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col items-start p-5 rounded-2xl bg-emerald-50/50 hover:bg-emerald-50 transition-colors border border-emerald-100/50"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="font-display font-semibold text-gray-900 text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Kamar Keluarga Section */}
      {(() => {
        const cmsCards = buildLandingRoomCards(rooms, content.rooms);
        const fallbackCards = familyRooms.map((room, idx) => {
          const liveRoom = rooms.find(r => r.name?.toLowerCase().includes(room.name.toLowerCase().split(' ')[0]));
          return {
            id: `family-fallback-${idx}`,
            name: room.name,
            image: room.image,
            price: liveRoom?.available_rate || liveRoom?.base_price || room.basePrice,
            capacity: room.capacity,
            bed: room.bed,
            size: room.size,
            description: room.description,
            features: room.features,
          };
        });
        const cards = cmsCards.length ? cmsCards : fallbackCards;
        return (
          <>
            <LandingRoomCardGrid
              title={content.rooms?.title || 'Pilihan Kamar Nyaman'}
              subtitle={content.rooms?.subtitle || 'Foto & Tipe Kamar Liburan Keluarga'}
              description={content.rooms?.description || 'Setiap kamar dirancang dengan interior hangat, sirkulasi udara pegunungan segar, serta kamar mandi bersih dengan air hangat yang aman untuk anak-anak.'}
              cards={cards}
              onBook={handleBookNow}
              onWhatsApp={handleWhatsApp}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 text-center">
              <Link
                to="/rooms"
                className="inline-flex items-center text-emerald-700 hover:text-emerald-800 font-semibold text-sm sm:text-base group"
              >
                Lihat Seluruh Tipe Kamar Lengkap & Galeri Foto
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </>
        );
      })()}

      {/* Kolam Renang & Fasilitas Anak Showcase */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Grid */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="rounded-2xl overflow-hidden shadow-lg h-60 sm:h-72">
                <img
                  src={showcaseContent.image_pool || "https://res.cloudinary.com/dgfjos8xa/image/upload/v1767362983/spencer-green-hotel-batu-malang-main-pool_udaykk.jpg"}
                  alt="Kolam Renang Anak & Dewasa Spencer Green Hotel Batu"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-60 sm:h-72 mt-8">
                <img
                  src={showcaseContent.image_breakfast || "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800"}
                  alt="Sarapan Lezat Keluarga di Kenari Restaurant"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-60 sm:h-72 -mt-4">
                <img
                  src={showcaseContent.image_playground || "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800"}
                  alt="Fasilitas Liburan Ramah Anak"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg h-60 sm:h-72 mt-4">
                <img
                  src={showcaseContent.image_cafe || "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800"}
                  alt="Suasana Cafe Rooftop & Taman Spencer Green Hotel"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            {/* Content Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
                Kesenangan & Relaksasi Buah Hati
              </span>

              <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                {showcaseContent.title || "Kolam Renang Jernih, Sarapan Sedap, & Area Ramah Keluarga"}
              </h2>

              <p className="text-gray-600 leading-relaxed">
                {showcaseContent.desc || "Di Spencer Green Hotel Batu, kami mengerti bahwa liburan keluarga yang sukses adalah saat anak-anak bergembira dan orang tua dapat beristirahat dengan damai."}
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <Waves className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-base">Kolam Renang Outdoor Berlatar Bukit</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Kolam renang luas dengan air pegunungan yang segar dan bersih. Area dangkal sangat cocok untuk si kecil bermain air dengan pengawasan orang tua.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <Utensils className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-base">Sarapan Prasmanan (Buffet) Kenari Restaurant</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Mulai hari dengan hidangan favorit keluarga: bubur ayam khas, roti panggang, sereal manis, aneka masakan nusantara, dan minuman segar.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-1">
                    <Coffee className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-base">Rintik Rindu Pool Cafe</h4>
                    <p className="text-gray-600 text-sm mt-1">
                      Orang tua bisa bersantai menikmati kopi hangat lokal dan camilan sambil mengawasi si kecil bermain di sekitar kolam renang.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex items-center gap-4">
                <Button
                  onClick={() => handleBookNow('Fasilitas Keluarga')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-6 py-5 rounded-full"
                >
                  Reservasi Liburan Keluarga
                </Button>
                <Button
                  onClick={() => handleWhatsApp('Tanya Sarapan & Pool')}
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-6 py-5 rounded-full"
                >
                  Tanya Fasilitas Lainnya
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lokasi Menuju Selecta & Tempat Wisata */}
      <section className="py-16 sm:py-20 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-emerald-300 font-semibold tracking-wider text-xs uppercase bg-emerald-800/80 px-3 py-1 rounded-full">
              Akses Lokasi Strategis
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-4">
              Hanya 5 Menit ke Taman Rekreasi Selecta
            </h2>
            <p className="text-emerald-200 text-base sm:text-lg">
              Hemat waktu Anda tanpa perlu bermacet-macetan dari pusat kota Batu. Spencer Green Hotel
              berada tepat di Jalan Raya Punten No.86, jalur utama paling praktis ke Selecta dan wisata alam sekitarnya.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {familyDestinations.map((dest, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-emerald-800/60 backdrop-blur-sm rounded-2xl overflow-hidden border border-emerald-700/60 hover:border-emerald-500 transition-all group"
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={dest.image}
                    alt={dest.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 bg-emerald-900/90 text-emerald-200 font-semibold text-xs px-2.5 py-1 rounded-full">
                    {dest.time}
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center text-emerald-400 text-xs font-medium mb-1">
                    <Compass className="w-3.5 h-3.5 mr-1" />
                    <span>Jarak: {dest.distance}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white mb-2">{dest.name}</h3>
                  <p className="text-emerald-200 text-xs leading-relaxed">{dest.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Map Callout Banner */}
          <div className="mt-12 bg-emerald-800/80 rounded-2xl p-6 sm:p-8 border border-emerald-700 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-left">
              <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-white text-base sm:text-lg">Ingin Petunjuk Arah Langsung?</h4>
                <p className="text-emerald-200 text-xs sm:text-sm">
                  Spencer Green Hotel berlokasi di Jl. Raya Punten No.86, Bumiaji, Kota Batu. Parkir kendaraan luas dan aman untuk mobil keluarga.
                </p>
              </div>
            </div>
            <a
              href="https://maps.google.com/?q=Spencer+Green+Hotel+Batu"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-emerald-900 hover:bg-emerald-50 font-bold px-6 py-3 rounded-full text-sm flex-shrink-0 transition-colors"
            >
              Buka Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Booking CTA Banner */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-emerald-700 via-emerald-800 to-emerald-900 text-white text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            {ctaContent.badge || "Penawaran Spesial Liburan Keluarga"}
          </span>
          <h2 className="font-display text-3xl sm:text-5xl font-bold mb-6">
            {ctaContent.title || "Jadikan Liburan Keluarga di Batu Lebih Berkesan"}
          </h2>
          <p className="text-emerald-100 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            {ctaContent.subtitle || "Dapatkan harga kamar terbaik langsung di website resmi Spencer Green Hotel Batu tanpa biaya tersembunyi."}
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Button
              onClick={() => handleBookNow('CTA Banner')}
              className="w-full sm:w-auto bg-white text-emerald-900 hover:bg-emerald-50 font-bold text-base px-8 py-6 rounded-full shadow-xl transition-all"
            >
              Cek Ketersediaan Kamar Sekarang
            </Button>
            <Button
              onClick={() => handleWhatsApp('CTA Banner WA')}
              variant="outline"
              className="w-full sm:w-auto border-white text-white hover:bg-white/10 font-medium text-base px-8 py-6 rounded-full"
            >
              Reservasi Cepat via WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-emerald-700 font-semibold tracking-wider text-xs uppercase bg-emerald-100 px-3 py-1 rounded-full">
              Pertanyaan Umum
            </span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-3 mb-2">
              FAQ Liburan Keluarga di Spencer Green Hotel Batu
            </h2>
            <p className="text-gray-500 text-sm">
              Semua yang perlu Anda ketahui untuk rencana staycation keluarga Anda.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-2xl overflow-hidden transition-colors"
                >
                  <button
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full text-left p-5 flex items-center justify-between font-semibold text-gray-900 hover:text-emerald-700 bg-white transition-colors"
                  >
                    <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="p-5 pt-0 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-emerald-50/20">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking Search Modal */}
      <BookingSearchModal
        isOpen={showBookingSearch}
        onClose={() => setShowBookingSearch(false)}
        onSearch={(data) => {
          navigate('/', {
            state: {
              doSearch: true,
              searchParams: data
            }
          });
        }}
      />
    </div>
  );
};

export default FamilyHotelBatu;
